import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import LightGestion from 'components/headers/lightGestion';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// currency formatting will be defined inside the component to access i18n

// Safely extract a readable text from mixed values (string/number/object)
const textOf = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') {
    const c = v;
    const nameCandidate = c.Nom || c.nom || c.fullName || c.full_name || c.username || c.title || c.label || (c.name && (c.name.fr || c.name.en || c.name)) || c.Name || c.clientName || c.ClientName;
    if (nameCandidate) return String(nameCandidate);
  }
  try { return String(v); } catch { return ''; }
};

const parseDateStr = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, '0');
    const mi = String(dt.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch {
    return String(d);
  }
};

// Build an absolute URL for images returned as relative storage paths
const toAbsImageUrl = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  // Already a data URL / blob or absolute http(s)
  if (s.startsWith('data:') || s.startsWith('blob:') || /^https?:\/\//i.test(s)) return s;
  // Base API URL (fallback to dev backend)
  const BASE = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:8000';
  if (s.startsWith('/storage/')) return `${BASE}${s}`;
  if (s.startsWith('storage/')) return `${BASE}/${s}`;
  // Common folder for POS cheque images
  if (s.startsWith('caisse/')) return `${BASE}/storage/${s}`;
  // Default: prefix with base
  return `${BASE}/${s.replace(/^\//, '')}`;
};

const getChequeNumber = (s) => (
  s?.chequeNumber || s?.NumCheque || s?.num_cheque || s?.cheque_number || s?.payment?.NumCheque || s?.payment?.num_cheque || s?.payment?.cheque_number || ''
);

const getChequeImage = (s) => (
  s?.chequeImage || s?.image || s?.cheque_image || s?.payment?.image || s?.payment?.cheque_image || ''
);

const normalizeSale = (s) => {
  const qty = Number(s.qty ?? s.quantity ?? s.Qte ?? 0);
  const unit = Number(s.unit_price ?? s.price_unit ?? s.unitPrice ?? s.PrixU ?? 0);
  const total = Number(s.total_line ?? s.total ?? s.TotalLigne ?? (qty * unit));
  const paymentStatus = s.payment?.PaymentStatus || s.payment_status || s.paymentMethod || s.payment_method || s.PaymentMethod || '';
  const productVal = s.product ?? s.Product ?? s.product_info;
  const clientVal = s.client ?? s.Client ?? s.customer ?? s.Customer ?? s.client_info;

  const productName = (
    s.product?.name?.fr || s.product?.Nom || s.product?.name || s.ProductName || s.Name?.fr || s.Name || s.name || textOf(productVal) || '—'
  );
  const clientName = (
    s.client_name || s.ClientName || s.customer_name || s.NomClient || s.ClientNom || textOf(clientVal) || '—'
  );
  return {
    id: s.id ?? s.ID ?? s._id ?? s.sale_id ?? s.NumeroVente ?? Math.random().toString(36).slice(2),
    number: s.sale_no ?? s.saleNumber ?? s.number ?? s.N ?? s.No ?? s.NumeroVente ?? '#',
    date: parseDateStr(s.date ?? s.created_at ?? s.Date),
    product: productName,
    client: clientName,
    payment: paymentStatus,
    qty,
    unitPrice: unit,
    lineTotal: total,
    paymentAmount: Number(s.payment_amount ?? s.paymentAmount ?? s.payment?.Amount ?? s.payment?.amount ?? 0),
    status: (s.status || 'normale').toLowerCase(),
    chequeNumber: getChequeNumber(s),
    chequeImage: getChequeImage(s),
  };
};

// (Deprecated) normalizeFromPair removed in favor of aggregated rows

// Build a single aggregated row for a sale that has multiple items
const normalizeSaleAggregated = (sale) => {
  const items = Array.isArray(sale?.items) ? sale.items : [];
  const names = items.map((it) => {
    const p = it?.product || it?.Product || {};
    return p?.Name?.fr || p?.name?.fr || p?.Name || p?.name || textOf(p);
  }).filter(Boolean);
  const qtySum = items.reduce((sum, it) => sum + Number(it?.qty ?? 0), 0);
  const clientName = sale?.client_name || sale?.ClientName || textOf(sale?.client) || '—';
  return {
    id: sale?.id ?? sale?.ID ?? Math.random().toString(36).slice(2),
    number: sale?.sale_no ?? sale?.saleNumber ?? sale?.id ?? sale?.ID ?? '#',
    date: parseDateStr(sale?.created_at ?? sale?.date ?? sale?.Date),
    product: names.length ? names.join(', ') : (textOf(sale?.product) || '—'),
    client: clientName,
    payment: sale?.payment_type || sale?.payment?.PaymentStatus || sale?.payment_status || '',
    qty: qtySum || Number(sale?.qty ?? 0),
    unitPrice: Number(items?.[0]?.price ?? items?.[0]?.unit_price ?? 0),
    lineTotal: Number(sale?.total ?? sale?.Total ?? 0),
    paymentAmount: Number(sale?.payment_amount ?? sale?.paymentAmount ?? sale?.payment?.Amount ?? sale?.payment?.amount ?? 0),
    status: (sale?.status || 'normale').toLowerCase(),
    chequeNumber: getChequeNumber(sale),
    chequeImage: getChequeImage(sale),
  };
};

// StatusPill removed (status column not displayed)

const PaymentPill = ({ method }) => {
  const { t } = useTranslation();
  const m = String(method || '').toLowerCase().trim();

  let cfg;
  if (m.includes('cash') || m.includes('comptant')) {
    cfg = { text: t('history.payment.cash', 'Cash'), color: '#16a34a', bg: '#dcfce7' };
  } else if (m.includes('cheque') || m.includes('chèque') || m.includes('cheq')) {
    cfg = { text: t('history.payment.cheque', 'Chèque'), color: '#2563eb', bg: '#dbeafe' };
  } else if (m.includes('credit') || m.includes('crédit') || m.includes('cred')) {
    cfg = { text: t('history.payment.credit', 'Crédit'), color: '#a16207', bg: '#fef3c7' };
  } else {
    cfg = { text: m || '—', color: '#6b7280', bg: '#f3f4f6' };
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: cfg.bg, color: cfg.color, padding: '6px 10px', borderRadius: 999, fontWeight: 700 }}>
      <span role="img" aria-label="coin">🪙</span> {cfg.text}
    </span>
  );
};

const Historique = () => {
  const { t } = useTranslation();
  const [clientQuery, setClientQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCheque, setShowCheque] = useState(false);
  const [chequeInfo, setChequeInfo] = useState({ number: '', image: '' });

  const currency = useCallback((v) => {
    const n = Number(v || 0);
    return `${n.toFixed(2)} ${t('stock.currency', 'MAD')}`;
  }, [t]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Attempt a likely endpoint; component is resilient to differences
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/sales', {
        headers,
        params: {
          client: clientQuery || undefined,
          product: productQuery || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
        }
      });
        console.log("res", res.data);

      const payload = res.data?.data ?? res.data; // support {data: [...]} or [...]
      const list = Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload) ? payload : []);
      const rows = list.map((sale) => {
        if (Array.isArray(sale?.items) && sale.items.length > 0) {
          return normalizeSaleAggregated(sale);
        }
        return normalizeSale(sale);
      });
      setSales(rows);
    } catch (e) {
      // If endpoint not available yet, keep empty list; UI will still work for demonstration and export of sample
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [clientQuery, productQuery, fromDate, toDate]);

  useEffect(() => {
    // initial load
    fetchHistory();
  }, [fetchHistory]);

  const visible = useMemo(() => {
    return sales.filter(s => {
      if (clientQuery && !String(s.client).toLowerCase().includes(clientQuery.toLowerCase())) return false;
      if (productQuery && !String(s.product).toLowerCase().includes(productQuery.toLowerCase())) return false;
      return true;
    });
  }, [sales, clientQuery, productQuery]);

  const totalAmount = useMemo(() => visible.reduce((sum, s) => sum + Number(s.lineTotal || 0), 0), [visible]);

  const resetFilters = () => {
    setClientQuery('');
    setProductQuery('');
    setFromDate('');
    setToDate('');
    fetchHistory();
  };

  const exportCSV = () => {
    const rows = [
      [
        t('history.csv.status', 'Statut'),
        t('history.csv.date', 'Date'),
        t('history.csv.number', 'N° Vente'),
        t('history.csv.product', 'Produit'),
        t('history.csv.client', 'Client'),
        t('history.csv.paymentMethod', 'Méthode de paiement'),
        t('history.csv.qty', 'Qté'),
        t('history.csv.unitPrice', 'Prix U.'),
        t('history.csv.lineTotal', 'Total Ligne')
      ],
      ...visible.map(s => [s.status, s.date, s.number, (Array.isArray(s.product)? s.product.map(p=> (typeof p==='object' ? (p?.Name?.fr||p?.name?.fr||p?.Name||p?.name||textOf(p)) : String(p))).join(' | ') : textOf(s.product)), s.client, s.payment, s.qty, s.paymentAmount, s.lineTotal])
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = t('history.csv.filename', 'historique-ventes.csv');
    a.click();
    URL.revokeObjectURL(url);
  };
  const isCheque = (method) => String(method || '').toLowerCase().includes('cheq');

  const openCheque = (row) => {
    setChequeInfo({ number: row?.chequeNumber || '', image: toAbsImageUrl(row?.chequeImage || '') });
    setShowCheque(true);
  };
  return (
    <AnimationRevealPage>
      <LightGestion />

      <div className="container  app-content" style={{ maxWidth: 1100 ,marginTop:"-60px",marginLeft:"-20px"}}>
        <h2 style={{ fontWeight: 900, fontSize: '30px', marginBottom: 6 }}>{t('history.title', 'Historique des Ventes')}</h2>

  {/* Filters card (options d'affichage removed) */}
  <div style={{ background: 'var(--card-bg)', borderRadius: 14, border: '1px solid var(--border)', padding: 16, boxShadow: '0 10px 24px rgba(15,23,42,0.04)' }}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">{t('history.filters.byClient', 'Filtrer par Client')}</label>
              <input value={clientQuery} onChange={e => setClientQuery(e.target.value)} type="text" className="form-control" placeholder={t('history.filters.clientPlaceholder', 'Rechercher un client...')} />
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('history.filters.byProduct', 'Filtrer par Produit')}</label>
              <input value={productQuery} onChange={e => setProductQuery(e.target.value)} type="text" className="form-control" placeholder={t('history.filters.productPlaceholder', 'Rechercher un produit...')} />
            </div>

            <div className="col-md-6">
              <label className="form-label">{t('history.filters.from', 'Du')}</label>
              <input value={fromDate} onChange={e => setFromDate(e.target.value)} type="date" className="form-control" />
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('history.filters.to', 'Au')}</label>
              <input value={toDate} onChange={e => setToDate(e.target.value)} type="date" className="form-control" />
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-primary" onClick={fetchHistory} disabled={loading}>
              {loading ? t('history.filters.filtering', 'Filtrage...') : t('history.filters.filter', 'Filtrer')}
            </button>
            <button className="btn btn-secondary" onClick={resetFilters}>{t('history.filters.reset', 'Réinitialiser')}</button>
          </div>
        </div>

        {/* Summary bar */}
  <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 14, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontWeight: 700 }}>
            <span role="img" aria-label="chart">📈</span>
            <span>{t('history.summary.found', '{{count}} ventes trouvées').replace('{{count}}', visible.length)}</span>
            <span role="img" aria-label="coin">🥇</span>
            <span>{t('history.summary.total', 'Total')}: <span style={{ color: '#60a5fa' }}>{currency(totalAmount)}</span></span>
          </div>
          <button className="btn btn-success" onClick={exportCSV}>{t('history.summary.export', 'Exporter')}</button>
        </div>

        {/* Table */}
        <div className="table-responsive" style={{ marginTop: 12 }}>
          <table className="table align-middle">
            <thead>
              <tr>
                <th>{t('history.table.date', 'DATE')}</th>
                <th>{t('history.table.number', 'N° VENTE')}</th>
                <th>{t('history.table.product', 'PRODUIT')}</th>
                <th>{t('history.table.client', 'CLIENT')}</th>
                <th>{t('history.table.paymentMethod', 'MÉTHODE DE PAIEMENT')}</th>
                <th className="text-end">{t('history.table.qty', 'QTÉ')}</th>
                <th className="text-end">{t('history.table.paymentAmount', 'MONTANT DU PAIEMENT')}</th>
                <th className="text-end">{t('history.table.lineTotal', 'TOTAL LIGNE')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">{loading ? t('history.table.loading', 'Chargement...') : t('history.table.none', 'Aucune vente trouvée')}</td>
                </tr>
              )}
              {visible.map((s) => (
                <tr key={s.id}>
                  <td>{s.date}</td>
                  <td>#{s.number}</td>
                  <td>{textOf(s.product)}</td>
                  <td style={{ textTransform: 'lowercase' }}>{s.client}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <PaymentPill method={s.payment || s.payment_type} />
                      {isCheque(s.payment || s.payment_type) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title={t('history.cheque.detailsTitle', 'Détails chèque')}
                          onClick={() => openCheque(s)}
                        >
                          🧾
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="text-end">{s.qty}</td>
                  <td className="text-end">{currency(s.paymentAmount)}</td>
                  <td className="text-end">{currency(s.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cheque Details Modal */}
      <Modal show={showCheque} onHide={() => setShowCheque(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('history.cheque.title', 'Détails du chèque')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
            <div className="mb-2"><strong>{t('history.cheque.number', 'Numéro du chèque:')}</strong> {chequeInfo.number ? chequeInfo.number : '----'}</div>
            <div>
              <strong>{t('history.cheque.image', 'Image:')}</strong>
              {chequeInfo.image ? (
                <div className="mt-2">
                  <img src={toAbsImageUrl(chequeInfo.image)} alt={t('history.cheque.imageAlt', 'Chèque')} style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid var(--border)' }} />
                </div>
              ) : (
                <div className="text-muted">{t('history.cheque.noImage', 'Aucune image')}</div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCheque(false)}>{t('history.cheque.close', 'Fermer')}</Button>
        </Modal.Footer>
      </Modal>

    </AnimationRevealPage>
  );
};

export default Historique;
